import { useEffect, useState } from "react";
import { CgArrowsExchange } from "react-icons/cg";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useLocation, useSearchParams } from "react-router-dom";

import { getSpecificRoute } from "api/vehicleRouteApi";
import DetailCard from "common/DetailCard";
import LoadingIndicator from "common/LoadingIndicator";
import ViaList from "common/ViaList";
import { DIR_VALUES, PAGE_FORMATTING_BASE } from "constants/common";
import {
  encodeWithSearchParams,
  getCategoryFromSubCategory,
} from "constants/helper";

const sideImg3 = window.ASSETS?.sideImg3 || "/assets/images/IMG_20200205_151637_3.jpg";

const VehicleRouteDetails = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const routeNumName = searchParams.get("route_num_name") || "";
  const routeCategory = searchParams.get("route_category");
  const boundDir = searchParams.get("bound_dir");

  const [routeSpecificData, setRouteSpecificData] = useState(null);
  const [currentDetails, setCurrentDetails] = useState(null);

  const getViaDetails = (dir_alignment) => {
    const [mainViaList, lastUTurn, diversions] = dir_alignment.split("  ||  ");
    return [mainViaList, lastUTurn, diversions];
  };

  const getVehicleRouteImgLink = (hash) => {
    return `https://drive.google.com/thumbnail?id=${hash}&sz=w1000`;
  };

  useEffect(() => {
    document.title = "Route details - Banglar Transit";
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getSpecificRoute(routeCategory, routeNumName).then((response) => {
      setRouteSpecificData(response.data);
    });
  }, [routeCategory, routeNumName]);

  useEffect(() => {
    if (routeSpecificData) {
      if (boundDir === routeSpecificData.bound_dir) {
        const [mainViaList, lastUTurn, diversions] = getViaDetails(
          routeSpecificData.same_dir_alignment
        );

        setCurrentDetails({
          destination1: routeSpecificData.destination_1,
          destination2: routeSpecificData.destination_2,
          mainViaList: mainViaList.split(", "),
          lastUTurn,
          diversions,
        });
      } else {
        const [mainViaList, lastUTurn, diversions] = getViaDetails(
          routeSpecificData.opp_dir_alignment
        );

        setCurrentDetails({
          destination1: routeSpecificData.destination_2,
          destination2: routeSpecificData.destination_1,
          mainViaList: mainViaList.split(", "),
          lastUTurn,
          diversions,
        });
      }
    }
  }, [routeSpecificData, boundDir]);

  return (
    <main className={PAGE_FORMATTING_BASE}>
      <a
        href={`${getCategoryFromSubCategory(routeCategory)}?sub_category=${encodeWithSearchParams(routeCategory)}`}
        className="flex items-center gap-2 mb-6 text-rose-700 hover:font-semibold"
      >
        <FaLongArrowAltLeft /> Go Back
      </a>

      <div className="flex flex-col md:flex-row gap-6 items-stretch">
        <DetailCard
          cardStyle="routeNum"
          detailHead={routeNumName}
          detailText={routeCategory}
          detailText2={routeSpecificData?.availability}
          linkContent={routeSpecificData?.vehicle_pic_link}
        />

        {routeSpecificData?.vehicle_pic_link && (
          <PhotoProvider maskOpacity={0.75}>
            <PhotoView
              render={({ scale, attrs }) => (
                <div>
                  <img
                    {...attrs}
                    alt="BusPic"
                    src={getVehicleRouteImgLink(
                      routeSpecificData?.vehicle_pic_link
                    )}
                    referrerPolicy="no-referrer"
                    style={{
                      position: "absolute",
                      transform: `translate(-50%, -50%) scale(${scale})`,
                      objectFit: "cover",
                      maxWidth: "100%",
                      maxHeight: "100%",
                    }}
                  />
                </div>
              )}
            >
              <div className="relative overflow-hidden rounded-md cursor-pointer group bg-black/10 h-28 md:h-auto md:w-32 ">
                <img
                  src={sideImg3}
                  alt="BusPic"
                  style={{ objectFit: "cover" }}
                  className="blur-md h-full w-full"
                  referrerPolicy="no-referrer"
                />

                <div className="absolute inset-0 flex flex-col justify-center items-center bg-black/40 text-white gap-5 opacity-80">
                  <span className="text-base">Bus Pic</span>
                  <span className="text-xs">Click to open</span>
                </div>
              </div>
            </PhotoView>
          </PhotoProvider>
        )}
      </div>

      {routeSpecificData ? (
        <div className="flex flex-col gap-8 mt-8 pb-20 md:pb-8">
          <div className="flex flex-col md:flex-row gap-8 md:gap-4">
            <DetailCard
              detailHead="Destination 1"
              detailText={currentDetails?.destination1}
              detailTextFontSize="text-4xl sm:text-3xl lg:text-4xl"
            />

            <div className="flex md:flex-col justify-evenly items-center">
              <span className="text-center text-rose-700 font-semibold text-2xl">
                {DIR_VALUES[boundDir]}
              </span>

              <button
                type="button"
                onClick={() => {
                  setSearchParams(
                    (searchParams) => {
                      searchParams.set(
                        "bound_dir",
                        searchParams.get("bound_dir") === "U" ? "D" : "U"
                      );
                      return searchParams;
                    },
                    { replace: true }
                  );
                }}
                className="self-center border border-1 text-rose-700 p-2 rounded-full bg-orange-100 shadow-lg"
                title="Click to change direction"
              >
                <CgArrowsExchange size={44} className="rotate-90 md:rotate-0" />
              </button>
            </div>

            <DetailCard
              detailHead="Destination 2"
              detailText={currentDetails?.destination2}
              detailTextFontSize="text-4xl sm:text-3xl lg:text-4xl"
            />
          </div>

          <section className="p-6 md:p-8 xl:p-10 text-lg bg-accent-back drop-shadow-lg rounded-md">
            <h2 className="text-rose-800 text-md font-semibold mb-5">
              Via / Alignment
            </h2>
            <div
              className={`text-2xl leading-[2.75rem] break-words tracking-normal xl:text-justify`}
            >
              <ViaList
                dest1={currentDetails?.destination1}
                dest2={currentDetails?.destination2}
                viaList={currentDetails?.mainViaList}
                lastUTurn={currentDetails?.lastUTurn}
                diversions={currentDetails?.diversions}
              />
            </div>
          </section>
        </div>
      ) : (
        <LoadingIndicator />
      )}
    </main>
  );
};

export default VehicleRouteDetails;
